import { useEffect, useState } from "react";
import {
  Button,
  Center,
  Checkbox,
  Code,
  Container,
  Group,
  Loader,
  NumberInput,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useLocation } from "wouter";
import {
  getSignupCode,
  createSignupCode,
  updateSignupCode,
  getSignupCodeRedemptions,
} from "../api";
import Timeago from "../../../Boilerplate/_Shared/Timeago";

function RedemptionsTable({ codeId }) {
  const [redemptions, setRedemptions] = useState(null);

  useEffect(() => {
    if (codeId) {
      getSignupCodeRedemptions(codeId).then(setRedemptions);
    }
  }, [codeId]);

  if (!redemptions) return null;
  if (redemptions.length === 0)
    return (
      <Text c="dimmed" size="sm" mt="md">
        No redemptions yet.
      </Text>
    );

  return (
    <Table mt="md" striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Email</Table.Th>
          <Table.Th>Tenant</Table.Th>
          <Table.Th ta="right">Credits</Table.Th>
          <Table.Th>When</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {redemptions.map((r) => (
          <Table.Tr key={r.id}>
            <Table.Td>{r.redeemedByEmail}</Table.Td>
            <Table.Td>
              <Code>{r.redeemedByTenantId}</Code>
            </Table.Td>
            <Table.Td ta="right">{r.creditsGranted}</Table.Td>
            <Table.Td>
              <Timeago date={r.idCreated} />
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

export default function SignupCodeEdit({ params }) {
  const isEditing = !!params?.id;
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [, setLocation] = useLocation();

  const form = useForm({
    initialValues: {
      code: "",
      creditsToGrant: 25000,
      sourceTenantId: "",
      maxRedemptions: 0,
      expiresAt: "",
      isActive: true,
      creditType: "default",
      creditValidityDays: 90,
      description: "",
      marketplaceId: "",
    },
  });

  useEffect(() => {
    if (isEditing) {
      getSignupCode(params.id).then((data) => {
        form.setValues({
          ...data,
          expiresAt: data.expiresAt
            ? new Date(data.expiresAt).toISOString().slice(0, 16)
            : "",
        });
        form.resetDirty();
        setLoading(false);
      });
    }
  }, [params?.id]);

  if (loading) {
    return (
      <Center mt="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Container size="sm">
      <Title order={1} mb="lg">
        {isEditing ? (
          <>
            Signup Code: <Code fz="xl">{form.values.code}</Code>
          </>
        ) : (
          "Create Signup Code"
        )}
      </Title>

      <form
        onSubmit={form.onSubmit(async (values) => {
          setSaving(true);
          try {
            const payload = {
              ...values,
              expiresAt: values.expiresAt
                ? new Date(values.expiresAt).toISOString()
                : null,
              marketplaceId: values.marketplaceId || null,
            };
            if (isEditing) {
              const data = await updateSignupCode(params.id, payload);
              form.setValues({
                ...data,
                expiresAt: data.expiresAt
                  ? new Date(data.expiresAt).toISOString().slice(0, 16)
                  : "",
              });
              form.resetDirty();
            } else {
              const data = await createSignupCode(payload);
              setLocation(`/admin/signup-codes/${data.id}`);
            }
            notifications.show({
              title: isEditing ? "Code updated" : "Code created",
              color: "green",
            });
          } finally {
            setSaving(false);
          }
        })}
      >
        <Stack>
          <TextInput
            label="Code"
            description="The code sellers will use to register (e.g. BNQ-250). Stored uppercase."
            required
            {...form.getInputProps("code")}
          />

          <TextInput
            label="Description"
            description="Internal note, also shown to the user on the registration page"
            {...form.getInputProps("description")}
          />

          <NumberInput
            label="Credits to grant"
            description="100 credits = 1 SKU"
            required
            min={100}
            step={100}
            thousandSeparator
            {...form.getInputProps("creditsToGrant")}
          />

          <TextInput
            label="Source Tenant ID"
            description="The marketplace tenant whose pool funds these credits"
            required
            {...form.getInputProps("sourceTenantId")}
          />

          <Group grow>
            <NumberInput
              label="Max redemptions"
              description="0 = unlimited"
              min={0}
              {...form.getInputProps("maxRedemptions")}
            />
            <NumberInput
              label="Credit validity (days)"
              description="How long the granted credits last"
              min={1}
              {...form.getInputProps("creditValidityDays")}
            />
          </Group>

          <TextInput
            label="Expires at"
            description="Leave empty for no expiry (ISO format)"
            type="datetime-local"
            {...form.getInputProps("expiresAt")}
          />

          <TextInput
            label="Marketplace ID"
            description="Optional: pre-select this marketplace for the seller"
            {...form.getInputProps("marketplaceId")}
          />

          <TextInput
            label="Credit type"
            {...form.getInputProps("creditType")}
          />

          <Checkbox
            label="Active"
            {...form.getInputProps("isActive", { type: "checkbox" })}
          />

          <Group mt="md">
            <Button type="submit" loading={saving}>
              {isEditing ? "Save Changes" : "Create Code"}
            </Button>
            <Button
              variant="default"
              onClick={() => setLocation("/admin/signup-codes")}
            >
              Cancel
            </Button>
          </Group>
        </Stack>
      </form>

      {isEditing && (
        <>
          <Title order={3} mt="xl" mb="sm">
            Redemptions
          </Title>
          <RedemptionsTable codeId={params.id} />
        </>
      )}
    </Container>
  );
}
